import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete,
  TextField,
  Input,
  Menu,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  SelectChangeEvent,
  Typography,
  Button,
  CircularProgress,
  FormControlLabel,
} from "@mui/material";
import styled from "styled-components";
import { Form, useParams } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../store";
import moment from "moment-timezone";
import { DateTime } from "luxon";
import { DatePicker } from "@mui/x-date-pickers";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatIsoToCustomDateStringWithEEEE } from "../utils/utils";
import { PrimaryButton, SecondaryButton } from "../subcomponents/CustomButton";
import { HelpTooltip } from "../subcomponents/HelpTooltip";
import { DateRangePicker } from "@mui/x-date-pickers-pro";
import { theme } from "../providers/themeProvider";
import CircleIcon from "@mui/icons-material/Circle";
import CloseIcon from "@mui/icons-material/Close";
import { Colors } from "../Colors";
import CheckIcon from "@mui/icons-material/Check";
import { debounce } from "lodash";
import LoadingWithMessage from "../subcomponents/LoadingWithMessage";
import CustomDatePicker from "../components/date-picker/DatePicker";
import PhoneNumberField from "../subcomponents/PhoneNumberField";
import CelebrationIcon from "@mui/icons-material/Celebration";
import DatePickerField from "../subcomponents/DatePickerField";

export const GiantCheckIcon = styled(CheckIcon)`
  font-size: 100px !important;
  color: #53c43b;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  margin-bottom: 20px;
  transition: transform 0.3s;
  border-radius: 20px;
  padding: 5px;

  &:hover {
    transform: scale(1.1);
  }
`;

export const GiantCelebrationIcon = styled(CelebrationIcon)`
  font-size: 100px !important;
  color: #ffd700;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  margin-bottom: 20px;
  transition: transform 0.3s;
  border-radius: 20px;
  padding: 5px;

  &:hover {
    transform: scale(1.1);
  }
`;

const ModalBox = styled(Box)`
  // display: flex;
  // flex-direction: column;
  // justify-content: center;
  // width: 100%;
  // margin: 10% auto;
  // background-color: #fff;
  // padding: 2%;
  // border-radius: 15px;
  // box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);

  // @media (max-width: 768px) {
  //   width: 70%;
  //   padding: 5%;
  // }
`;

const ModalScrollBox = styled(Box)`
  // display: flex;
  // flex-direction: column;
  // width: 30%;
  // margin: 10% auto;
  // background-color: #fff;
  // padding: 2%;
  // border-radius: 15px;
  // box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  // max-height: 70%;
  // overflow-y: auto;

  // @media (max-width: 768px) {
  //   width: 70%;
  //   padding: 5%;
  // }
`;

//const timezones = moment.tz.names();
const timezones = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles"];

const timezoneOptions = timezones.map((tz) => {
  const dt = DateTime.now().setZone(tz);
  const offsetString = dt.toFormat("ZZ");
  const text = `${tz} (UTC${offsetString})`;

  return { value: tz, label: text, offset: dt.offset };
});

timezoneOptions.sort((a, b) => a.offset - b.offset);

const SelfAddForm: React.FC = () => {
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [dob, setDob] = useState<DateTime | null>(null);
  const [addedPatient, setAddedPatient] = useState<boolean>(false);
  const [uploadingPatient, setUploadingPatient] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const { orgId } = useParams<{ orgId: string }>();

  const createWaitlistRequest = async () => {
    try {
      setUploadingPatient(true);
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/requests`, {
        firstName,
        lastName,
        dateOfBirth: dob?.toFormat("yyyy-MM-dd"),
        organizationId: orgId,
      });

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to create waitlist request");
      }

      setFirstName("");
      setLastName("");
      setDob(null);
      setAddedPatient(true);
    } catch (error: any) {
      console.error("Error creating waitlist request:", error);
      setErrorMsg(error.message || "Failed to create waitlist request");
    } finally {
      setUploadingPatient(false);
    }
  };

  const debouncedSetFirstName = debounce((value: string) => {
    setFirstName(value);
  }, 500);

  const debouncedSetLastName = debounce((value: string) => {
    setLastName(value);
  }, 500);

  return (
    <Box
      sx={{
        backgroundColor: "#0D0F23",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100dvh",
        width: "100%",
        margin: 0,
      }}
    >
      <Box
        sx={{
          overflowY: "auto",
          padding: "20px",
          backgroundColor: "#1A1E36",
          borderRadius: "30px",
          width: "100%",
          maxWidth: "800px",
          margin: "20px",
        }}
      >
        {addedPatient && (
          <>
            <Box
              sx={{
                backgroundColor: "#1C1F3E",
                padding: "40px 20px",
                borderRadius: "10px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                width: "100%",
                maxHeight: "60vh",
                textAlign: "center",
                overflowX: "hidden",
                boxSizing: "border-box",
              }}
            >
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "1.5rem",
                  color: "#FFFFFF",
                  marginBottom: "20px",
                }}
              >
                The clinic recieved your request, you will be added shortly!
              </Typography>
            </Box>
            {/* Powered by Logo */}
            <Box display="flex" justifyContent="right" alignItems="center" marginTop="10px">
              <Box display="flex" alignItems="center">
                <a href="https://penciled.com" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                  <Typography variant="caption" color={Colors.info} marginRight="5px">
                    Powered by Penciled
                  </Typography>
                </a>
              </Box>
            </Box>
          </>
        )}

        {!addedPatient && (
          <>
            <Box>
              {/* Waitlist Text */}
              <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} marginTop={"10px"}>
                <Typography margin={"10px 0"} fontWeight={"bold"} fontSize={"1.2rem"}>
                  Add Yourself to the Waitlist
                </Typography>
              </Box>
            </Box>

            <Typography variant="body1" sx={{ marginTop: "10px" }}>
              Patient Information
            </Typography>

            <Box display="flex" justifyContent="space-between" flexWrap="nowrap" gap="4px" marginTop="8px" marginBottom="20px">
              <TextField
                onChange={(e) => debouncedSetFirstName(e.target.value)}
                variant="outlined"
                placeholder="First Name"
                sx={{
                  height: "50px !important",
                  flexBasis: "calc(50% - 2px)",
                  borderRadius: "10px",
                  background: "rgba(255, 255, 255, 0.04)",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    height: "100%",
                  },
                  "& .MuiInputBase-input": {
                    padding: "8px 12px",
                    fontSize: "0.9rem",
                    color: "#FFFFFF",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#FFF",
                  },
                }}
              />
              <TextField
                onChange={(e) => debouncedSetLastName(e.target.value)}
                variant="outlined"
                placeholder="Last Name"
                sx={{
                  height: "50px !important",
                  flexBasis: "calc(50% - 2px)",
                  borderRadius: "10px",
                  background: "rgba(255, 255, 255, 0.04)",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    height: "100%",
                  },
                  "& .MuiInputBase-input": {
                    padding: "8px 12px",
                    fontSize: "0.9rem",
                    color: "#FFFFFF",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#FFF",
                  },
                }}
              />
            </Box>
            <DatePicker
              value={dob}
              onChange={(newValue) => newValue && setDob(newValue)}
              label="Date of Birth"
              sx={{
                flexBasis: "100%",
                width: "50%",
                height: "50px",
                marginTop: "8px",
                borderRadius: "8px",
                background: Colors.textfield,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  padding: "0 12px",
                  height: "34px",
                  boxSizing: "border-box",
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.2)",
                  },
                  "&:hover fieldset": {
                    borderColor: Colors.primary,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: Colors.primary,
                  },
                },
                "& .MuiInputBase-root": {
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputBase-input": {
                  padding: "8px 12px",
                  color: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  boxSizing: "border-box",
                },
                "& .MuiInputLabel-root": {
                  color: Colors.info,
                  top: "-6px",
                },
                "& .MuiSvgIcon-root": {
                  color: Colors.info,
                  marginRight: "8px",
                },
              }}
            />

            {errorMsg && (
              <Typography variant="body1" color="error" mt={2}>
                {errorMsg}
              </Typography>
            )}

            <PrimaryButton
              disabled={!firstName || !lastName || !dob}
              onClick={createWaitlistRequest}
              sx={{
                marginTop: "20px !important",
                paddingTop: "15px !important", // Increase padding for better vertical space
                padding: "15px", // Increase padding for better vertical space
                width: "100%", // Make the button take the full width of the parent
                "&:disabled": {
                  backgroundColor: "#31364B",
                  color: "#9FA1A6",
                },
              }}
            >
              {uploadingPatient ? <CircularProgress size={24} color="inherit" /> : "Join Waitlist"}
            </PrimaryButton>

            {/* Powered by Logo */}
            <Box display="flex" justifyContent="right" alignItems="center" marginTop="10px">
              <Box display="flex" alignItems="center">
                <a href="https://penciled.com" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                  <Typography variant="caption" color={Colors.info} marginRight="5px">
                    Powered by Penciled
                  </Typography>
                </a>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SelfAddForm;
